import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    ClientMode,
    DateFilterItem,
    DateFilterItemDefinition,
    EditorCreateEvent,
    EditorEventType,
    EditorLayersMode,
    EditorTool,
    EditorToolType,
    EditorUpdateEvent,
    ExtendedEditorToolDefinition,
    FeatureId,
    FeaturePointer,
    GroupFeaturePointer,
    LayerPrintMode,
    MapEditor,
    MapEditorActions,
    FeatureFilterType,
    MapInteractionType,
    FeatureZoom,
    FeatureFilter,
    FieldProps,
    KeyboardShortcut,
    LayerId,
    EditorToolDefinition,
    FeatureMultipleSelector,
    ActionTypes,
    FeatureIdType,
    EditorEvent,
    MeasurementCardData,
} from '@mc/react-mapio';
import {useAuth} from '@mc/react-auth';
import {Modal, AppError, showConfirm, Spinner, toaster, BlankPage, useFinalTableData} from '@mc/ui-v4';
import {useAppConfig, useNastaveni} from '@mc/react-app-base';
import {AttributesModal} from '../components/forms/AttributesModal';
import {AttributeTableFinal} from '../components/tables/AttributeTableFinal';
import {ClientAttributeTableFinal} from '../components/tables/ClientAttributeTableFinal';
import {RouteComponentProps, useHistory, useLocation} from 'react-router';
import {booleanToString, flattenEnumObject, removeUncontained, simplifyProperties, findLayer} from '../helpers/DataMutators';
import {Sidebar} from '../components/sidebar/Sidebar';
import {useCatalogLayers} from '../hooks/useCatalogLayers';
import {
    AkceApi,
    DocumentApi,
    DocumentVO,
    FeatureWrapperVO,
    LayerAttributeVO,
    LayerDetailVO,
    LayerVO,
    RightsApi,
    TopicVisibility,
    TopicVO,
    UserLayersApi,
    UserRightsVO,
} from '../api';

import {Toolbar} from '../components/toolbar/Toolbar';
import {ETriggerAction, Trigger} from '../helpers/Trigger';
import {MeasurementInfo} from '../components/sidebar/content/MeasurementSidebar';
import {flattenTools} from '../helpers/FlattenTools';
import {Overhead} from '../components/overhead/Overhead';
import {focusMapIframe, IFRAME_CLASSNAME, sendEvent, sendReadyEvent} from '../helpers/EventUtils';
import {addEditTools, toolbarFullDef, toolbarPublicDef} from '../components/toolbar/ToolbarConst';
import {GeometryUndoStep, MAX_UNDO_STEPS, UNDO_TOOL_LABELS} from '../components/toolbar/undoTypes';
import {Feature} from 'geojson';
import {useMapioContext} from '../mapioContext';
import {useLayersConfig} from '../hooks/useLayersConfig';
import {useOpravneniQuery} from '../hooks/useOpravneniQuery';
import {QueryClient, useQueryClient, UseQueryResult} from 'react-query';
import {CACHE_KEY_FEATURE_DETAIL} from '../hooks/useFeatureDetail';
import {CACHE_KEY_LAYER_DETAIL} from '../hooks/useLayerDetail';
import {actionApiCall, rffFormSubmit, WaitForQueryData} from '@mc/react-app-v4';
import {ApiCallWaitingModal} from '../components/modals/ApiCallWaitingModal';
import {CoordinatesProps} from '../components/sidebar/content/CoordinatesSidebar';
import './MapPage.scss';
import {DistanceData, ZoomSelector} from '../components/footer/ZoomSelector';
import {MeasurementCard, HighlightedEdge} from '../components/footer/MeasurementCard';
import {LineOffsetModal} from '../components/forms/LineOffsetModal';
import {MultipleFeaturesSelection} from '../components/modals/MultipleFeaturesSelection';
import {useTopicsMap} from '../hooks/useTopicsMap';
import {PerpendicularLineModal} from '../components/forms/PerpendicularLineModal';
import LightboxGallery from '../components/image/LightboxGallery';
import {CACHE_FEATURES_IDS} from '../hooks/useFeatureIds';
import {useIsMobile} from '../hooks/useIsMobile';
import {InformationalFooter} from '../components/footer/InformationalFooter';
import {OVERLAY_LAYER_ID} from '../const';
import {getUserLayerActions} from '../helpers/userLayerActions';
import {canDeleteFeatures, computeEnabledTools, filterToolsByEnabled, isSelectTool} from '../helpers/ToolEnablement';
import {convertContextToParams, updateContext} from '../helpers/UpdateContext';
import {getDefaultDateFilterFromQueryParams, getDefaultDateFilterId} from '../helpers/DateFilters';
import {MAPA_PAGE_KEY} from '../const/PageKeys';
import {getDtmDetailUrl} from '../utils/urlRedirectUtils';
import {formatExternalLayersToCatalogItemVO} from '../helpers/Formatter';
import {CACHE_KEY_MAP_SETTINGS, CACHE_KEY_CURRENT_TOPIC, MapViewState} from '../helpers/MapSettingsUtils';
import {ATTRIBUTE_TABLE_FT_KEY} from '../const/FinalTableKeys';
import {getEffectiveLayerId} from '../helpers/LayerConfigUtils';
import {affectedLayerIds, confirmBulkMove, persistMovedFeaturesInBackground, updateFeaturesBatched} from '../helpers/featureUpdates';

const isReactNative = globalThis.ReactNativeWebView;

type Props = RouteComponentProps<{
    kodOrganizace?: string;
}>;

/** Mapová komponenta */
export const MapPage: React.FC<Props> = ({
    match: {
        params: {kodOrganizace},
    },
}) => {
    const mapioContext = useMapioContext();

    const [savedMapViewState, setSavedMapViewState] = useNastaveni<MapViewState>(CACHE_KEY_MAP_SETTINGS, {});
    const [savedCurrentTopic, setSavedCurrentTopic] = useNastaveni<string>(CACHE_KEY_CURRENT_TOPIC, null);
    const savedMapViewStateRef = useRef(savedMapViewState);
    savedMapViewStateRef.current = savedMapViewState;
    const setSavedMapViewStateRef = useRef(setSavedMapViewState);
    setSavedMapViewStateRef.current = setSavedMapViewState;

    // @ts-ignore
    const {serverPublic, catalogMenuVisible, podporaKolekci} = useAppConfig();
    const isMobile = useIsMobile();
    const history = useHistory();
    const {data: auth} = useAuth();
    const layersConfigQuery: UseQueryResult<{[key: number]: LayerVO}> = useLayersConfig();
    const ref = useRef<MapEditorActions>(null);
    // Re-aktivace extent filtru pro panel hledání. Funkci sem registruje SearchPanel (když je otevřený);
    // MapPage ji volá při pohybu mapou (mapové eventy chodí jen sem). Ref → žádné re-rendery, nulová logika v MapPage.
    const searchExtentRefreshRef = useRef<(() => void) | null>(null);
    // Téma, pro které už bylo aplikováno výchozí nastavení mapy. Brání tomu, aby refetch
    // konfigurace (nová reference topic objektu) znovu přepsal živý stav vrstev na výchozí.
    const appliedTopicIdRef = useRef<string | null>(null);
    // Téma, které jsme naposledy poslali mapovému klientovi. Ten si drží vlastní téma (katalog,
    // aktivní vrstvy, objekty) a o přepnutí musí vědět, jinak v mapě zůstane předchozí téma.
    const syncedTopicIdRef = useRef<string | null>(null);
    const [isTempEditingStarted, setIsTempEditingStarted] = useState<boolean>(false);
    const [isExtentChecked, setIsExtentChecked] = useState(false);
    const [isProvazatChecked, setIsProvazatChecked] = useState(true);
    const [extentFeatures, setExtentFeatures] = useState<FeatureId[]>([]);
    /** Pointery objektů ve výřezu (s layerId u každého) – režim "všechny zapnuté vrstvy" pro panel hledání. Oddělené od extentFeatures, aby neovlivnily AttributeTableFinal. */
    // undefined = engine ještě neposlal EXTENT_FEATURES (rozlišujeme „ještě nenačteno" od „načteno, prázdno").
    const [extentFeaturePointers, setExtentFeaturePointers] = useState<GroupFeaturePointer[] | undefined>(undefined);
    /** LayerId pro setLayerFeaturesFilter – diskriminovaná vrstva má přednost před selectedLayerId */
    const [activeFilterLayerId, setActiveFilterLayerId] = useState<LayerId>(null);
    const [toolsEnabled, setToolsEnabled] = useState<string[]>([]);
    const [checkedLayerId, setCheckedLayerId] = useState<LayerId>(null);
    const [coordinateLocation, setCoordinateLocation] = useState<CoordinatesProps>(null);
    const [detailLayerId, setDetailLayerId] = useState<LayerId>(null);
    const [detailFeatureId, setDetailFeatureId] = useState<FeatureId>('');
    const [distanceData, setDistanceData] = useState<DistanceData>({currentDistance: null, currentPixelDistance: 0, currentZoomLevel: 0});
    const [backgroundLayer, setBackgroundLayer] = useState<LayerId>(null);
    const [foregroundLayer, setForegroundLayer] = useState<LayerId>(null);
    const [snappingActive, setSnappingActive] = useState<boolean>(false);
    const [contextTools, setContextTools] = useState<(EditorTool | EditorToolDefinition)[]>([EditorTool.SHOW_COORDS, EditorTool.EDIT, EditorTool.EXACT_SELECT]);
    const [isTopicChangeEnabled, setIsTopicChangeEnabled] = useState<boolean>(true);
    const [isCatalogMenuVisible, setIsCatalogMenuVisible] = useState<boolean>(true);
    const [noToolbar, setNoToolbar] = useState<boolean>(false);
    const [featureTooltips, setFeatureTooltips] = useState<boolean>(false);
    const [color, setColor] = useState<any>(null);
    const [geometryMeasurementData, setGeometryMeasurementData] = useState<MeasurementCardData | null>(null);
    const [measurementFeaturePointers, setMeasurementFeaturePointers] = useState<FeaturePointer[]>([]);
    const [highlightedMeasurementEdge, setHighlightedMeasurementEdge] = useState<HighlightedEdge | null>(null);
    const [searchPanelBackToList, setSearchPanelBackToList] = useState<(() => void) | null>(null);
    // Historie změn geometrie pro tlačítko "Zpět"/"Vpřed" (MCW-28392). Pouze klientská – zaniká refreshem/zavřením stránky.
    const [undoStack, setUndoStack] = useState<GeometryUndoStep[]>([]);
    const [redoStack, setRedoStack] = useState<GeometryUndoStep[]>([]);

    const {
        activeTool,
        selectedDetailFeature,
        writableLayers,
        activeLayers,
        layersMode,
        currentTools,
        selectedLayerId,
        topicId,
        isIframe,
        disabled,
        insertionLayerId,
        attributeTableDiscriminatedChildId,
        isAttributeTableOpen,
        isClientAttributeTableOpen,
        clientAttributeTableLayerId,
    } = mapioContext.value;
    const {
        setDisabled,
        setActiveTool,
        setSelectedDetailFeature,
        setWritableLayers,
        setActiveLayers,
        setLayersMode,
        setCurrentTools,
        setSelectedLayerId,
        setIsAttributeTableOpen,
        setIsClientAttributeTableOpen,
        openAttributeTable,
    } = mapioContext;

    /** Nastavení možného filtrování podle datumu */
    const [dateFilterItems, setDateFilterItems] = useState<DateFilterItemDefinition[]>([
        DateFilterItem.TODAY,
        DateFilterItem.TODAY_AND_FUTURE,
        DateFilterItem.UNLIMITED,
        DateFilterItem.TODAY_PLUS_TWO_WEEKS,
        DateFilterItem.TODAY_PLUS_MONTH,
        DateFilterItem.TODAY_PLUS_MINUS_WEEK,
        DateFilterItem.CUSTOM_RANGE,
        DateFilterItem.CUSTOM_DATE,
    ]);
    const [isImageGalleryOpen, setIsImageGalleryOpen] = useState<boolean>(false);
    const [imageEventData, setImageEventData] = useState<any>(null);

    const topicsMapQuery: UseQueryResult<TopicVO[]> = useTopicsMap();
    const queryClient: QueryClient = useQueryClient();
    const attrTableFtKey = `${ATTRIBUTE_TABLE_FT_KEY}-${selectedLayerId ?? 'none'}-${attributeTableDiscriminatedChildId ?? 'none'}`;
    const attrFinalTableData = useFinalTableData(attrTableFtKey);
    let {search} = useLocation();
    const query = new URLSearchParams(search);

    // Inicializace dateFilterRange na základě query parametrů
    const initialDateFilterRange = getDefaultDateFilterFromQueryParams(Object.fromEntries(query.entries()));
    const [dateFilterRange, setDateFilterRange] = useState<FeatureFilter>(initialDateFilterRange);

    // Pokud přijdou custom dateFilterItems eventem, přepočítej default
    useEffect(() => {
        if (query.get('defaultDateFilter') && dateFilterItems.some(item => typeof item === 'object')) {
            const dateFilterFromQuery = getDefaultDateFilterFromQueryParams(Object.fromEntries(query.entries()), dateFilterItems);
            setDateFilterRange(dateFilterFromQuery);
            ref.current?.setFilter(dateFilterFromQuery);
        }
    }, [dateFilterItems]); // eslint-disable-line react-hooks/exhaustive-deps
    const [optimalPrintScale, setOptimalPrintScale] = useState<FieldProps>(null);

    useEffect(() => {
        if (ref.current && !mapioContext.value.mapRef) {
            mapioContext.setMapRef(ref);
        }
    }, [ref.current]);

    const catalogLayersQuery = useCatalogLayers(topicId);

    const currentTopic: TopicVO = useMemo(
        () => topicsMapQuery.data?.find((item: TopicVO) => item?.id === topicId) ?? null,
        [topicsMapQuery.data, topicId],
    );

    useEffect(() => {
        if (currentTopic && mapioContext.value.topic !== currentTopic) {
            mapioContext.setTopic(currentTopic);
        }
    }, [currentTopic]);

    /**
     * Pojistka, kdyby se do topicId dostalo téma, které v konfiguraci není (např. neplatné téma v URL).
     * Musí to být v effectu a jen nad dokončeným dotazem - během refetche konfigurace (hned po založení
     * nového tématu) vidí komponenta ještě starý seznam témat a přepnutí na první dostupné téma by
     * zrušilo přepnutí na právě založené téma (MCW-29126).
     */
    useEffect(() => {
        if (!topicId || currentTopic || !topicsMapQuery.isFetched || topicsMapQuery.isFetching || !topicsMapQuery.data?.length) {
            return;
        }
        console.warn('[MapPage] Téma neexistuje v konfiguraci, nahrazuji prvním dostupným.', {
            pozadovaneTopicId: topicId,
            zvoleneTopicId: topicsMapQuery.data[0]?.id,
        });
        mapioContext.setTopicId(topicsMapQuery.data[0].id);
    }, [topicId, currentTopic, topicsMapQuery.isFetched, topicsMapQuery.isFetching, topicsMapQuery.data]);

    // Téma s useThemeDefaults nikdy nepoužije uložený stav mapy z nastavení (useNastaveni), vždy jen výchozí hodnoty tématu.
    const useThemeDefaults = currentTopic?.useThemeDefaults ?? false;
    const savedViewForTopic = useThemeDefaults ? undefined : savedMapViewState?.[topicId];

    /**
     * Centralizované nastavení mapy při změně topicu
     * Zajišťuje vyčištění vrstev z předchozího topicu a nastavení hodnot pro nový topic
     */
    useEffect(() => {
        if (!disabled && ref.current && topicId && currentTopic && catalogLayersQuery.isFetched && layersConfigQuery.isFetched) {
            // O přepnutí tématu musí vědět i mapový klient - drží si vlastní téma (katalog, aktivní vrstvy,
            // objekty). Např. přepnutí tématu ve správě témat mění jen stav obálky, takže bez tohoto by
            // v mapě zůstaly vrstvy předchozího tématu (MCW-29126). Pokud už na daném tématu je, je zpráva
            // pro klienta no-op; při skutečném přepnutí ale mapa zahodí vrstvy, takže je nutné je znovu
            // aplikovat (proto rušíme příznak aplikovaného tématu).
            if (syncedTopicIdRef.current !== topicId) {
                ref.current.setTopic(topicId);
                syncedTopicIdRef.current = topicId;
                appliedTopicIdRef.current = null;
            }
            // Výchozí nastavení mapy aplikujeme jen jednou na dané téma. Bez tohoto guardu by
            // refetch konfigurace (nová reference topic objektu) effect znovu spustil a přepsal
            // živý stav vrstev na výchozí – uživateli by se objevily i vrstvy vypnuté v katalogu.
            if (appliedTopicIdRef.current === topicId) {
                return;
            }
            const topic = currentTopic;
            const savedView = savedViewForTopic;
            const catalogData = catalogLayersQuery.data || [];
            const layersConf = layersConfigQuery.data ?? {};
            // Validace uložených vrstev proti katalogu.
            // Label vrstvy (sourceLayerId != null) nejsou v katalogu, ale jsou platné – necháme je projít.
            let validatedLayers = savedView?.activeLayers ?? topic.selectedLayers ?? [];
            if (validatedLayers.length > 0 && catalogData.length > 0) {
                validatedLayers = validatedLayers.filter(layerId => findLayer(catalogData, layerId) || layersConf[layerId]?.sourceLayerId != null);
            }
            if (savedView?.activeLayers == null) {
                const labelLayerIds: LayerId[] = [];
                validatedLayers.forEach(id => {
                    const numId = Number(id);
                    const entry = Object.entries(layersConf).find(([, cfg]) => cfg.sourceLayerId === numId);
                    if (entry) {
                        const labelId = Number(entry[0]);
                        if (!validatedLayers.includes(labelId) && !labelLayerIds.includes(labelId)) {
                            labelLayerIds.push(labelId);
                        }
                    }
                });
                validatedLayers = [...validatedLayers, ...labelLayerIds];
            }

            // Nastavení výchozích hodnot z topicu nebo uloženého stavu
            const zoom = savedView?.zoom ?? topic.initialZoom;
            const center = savedView?.center ?? [topic.initialLon, topic.initialLat];
            const layers = validatedLayers;
            const bgLayer = savedView?.backgroundLayer ?? topic.defaultBackgroundLayer ?? null;
            const fgLayer = savedView?.foregroundLayer ?? topic.defaultForegroundLayer ?? null;
            // Aplikace nastavení na mapu
            ref.current.setZoom(zoom);
            ref.current.setLocation(center);
            ref.current.setLayers(layers);
            // Hodnota 0/null je pro mapový klient pokyn "žádná podkladová vrstva" - vrstvu odebere
            // a žádnou nepřidá. Pokud podkladovou/popisnou vrstvu neurčuje ani uložený stav mapy,
            // ani konfigurace tématu, zprávu neposíláme a necháme mapový klient použít výchozí
            // vrstvu tématu (jinak by mapa po přepnutí tématu zůstala bez podkladové mapy).
            // Lokální stav se dorovná zprávou o změně vrstvy z mapového klienta.
            if (bgLayer != null) {
                ref.current.setBackgroundLayer(bgLayer);
                setBackgroundLayer(bgLayer);
            }
            if (fgLayer != null) {
                ref.current.setForegroundLayer(fgLayer);
                setForegroundLayer(fgLayer);
            }

            // Aktualizace lokálního stavu
            setActiveLayers(layers);

            // Označíme téma jako aplikované – další re-fire effectu pro stejné téma už stav nepřepíše.
            appliedTopicIdRef.current = topicId;
        }
    }, [disabled, topicId, currentTopic, catalogLayersQuery.isFetched, layersConfigQuery.isFetched]);

    /**
     * Ukládání aktuálního topicu a stavu mapy do nastavení (výřez z eventu CENTER_AND_ZOOM_CHANGED)
     */
    const saveMapViewState = useCallback(
        ({zoom, center}: {zoom: number; center: [number, number]}) => {
            if (!topicId) return;

            // U témat s useThemeDefaults se stav mapy neukládá, po návratu se vždy použijí výchozí hodnoty tématu
            if (!useThemeDefaults) {
                setSavedMapViewStateRef.current({
                    ...savedMapViewStateRef.current,
                    [topicId]: {
                        zoom,
                        center,
                        activeLayers,
                        backgroundLayer,
                        foregroundLayer,
                    },
                });
            }
            if (topicId !== savedCurrentTopic) {
                setSavedCurrentTopic(topicId);
            }
        },
        [topicId, useThemeDefaults, activeLayers, backgroundLayer, foregroundLayer],
    );

    const savePartialMapViewState = useCallback(
        (partialView: Partial<MapViewState[string]>) => {
            if (!topicId || useThemeDefaults) return;
            setSavedMapViewStateRef.current({
                ...savedMapViewStateRef.current,
                [topicId]: {
                    ...savedMapViewStateRef.current?.[topicId],
                    ...partialView,
                },
            });
        },
        [topicId, useThemeDefaults],
    );

    // Uložení i při pouhé změně vrstev (bez pohybu mapou). Až poté, co se na téma aplikoval uložený/výchozí
    // stav (efekt výše) – jinak by se hned po přepnutí tématu uložil pod nový topicId ještě stav toho předchozího.
    useEffect(() => {
        if (isIframe || !topicId || useThemeDefaults || appliedTopicIdRef.current !== topicId) {
            return;
        }
        savePartialMapViewState({activeLayers, backgroundLayer, foregroundLayer});
    }, [activeLayers, backgroundLayer, foregroundLayer, topicId, isIframe, useThemeDefaults, savePartialMapViewState]);

    const initialSavedView = savedViewForTopic;

    useEffect(() => {
        const base64Context = query.get('context') || '';
        let context = updateContext(mapioContext.value.context, base64Context);
        // Přidání kodOrganizace do kontextu, pokud existuje
        if (serverPublic && kodOrganizace) {
            context = updateContext(context, {kodOrganizace});
        }
        const instanceId = query.get('instanceId');
        const clientMode = query.get('clientMode');
        const topicChangeEnabled = query.get('topicChangeEnabled');
        const noToolbar = query.get('noToolbar');
        const isIframe = globalThis.Cypress ? false : globalThis.self !== globalThis.top;
        const noAppbar = query.get('noAppbar');
        let topic = query.get('topic') ?? mapioContext.value?.topicId ?? savedCurrentTopic;
        // Téma z URL / uloženého nastavení použijeme jen pokud v konfiguraci opravdu je.
        if (topic && topicsMapQuery.data?.length > 0 && !topicsMapQuery.data.some((item: TopicVO) => item.id === topic)) {
            console.warn('[MapPage] Téma neexistuje v konfiguraci, použiji první dostupné.', {
                pozadovaneTopicId: topic,
                dostupneTopicId: topicsMapQuery.data.map((item: TopicVO) => item.id),
            });
            topic = null;
        }
        // Pokud je v url parametr topic, ale neexistuje v konfiguraci, tak se vezme první z konfigurace
        if (!topic && topicsMapQuery.data && topicsMapQuery.data.length > 0) {
            topic = topicsMapQuery.data[0]?.id;
        }
        let decodedNoAppbar;
        if (noAppbar != null) {
            const decodedValue = decodeURIComponent(noAppbar);
            decodedNoAppbar = decodedValue === 'true' || decodedValue === '1';
        }
        mapioContext.set({
            ...mapioContext.value,
            topicId: topic ?? mapioContext.value?.topicId ?? null,
            topic: topicsMapQuery.data?.find(t => t.id === topic) ?? mapioContext.value?.topic ?? null,
            instanceId,
            clientMode: clientMode as ClientMode,
            isIframe: decodedNoAppbar ?? isIframe,
            context,
            currentTools,
        });
        if (topicChangeEnabled != null) {
            const decodedValue = decodeURIComponent(topicChangeEnabled);
            setIsTopicChangeEnabled(decodedValue !== 'false');
        }
        if (noToolbar != null) {
            const decodedValue = decodeURIComponent(noToolbar);
            setNoToolbar(decodedValue === 'true' || decodedValue === '1');
        }
    }, [search, auth, topicsMapQuery.data]);

    useEffect(() => {
        if (!serverPublic) {
            return;
        }
        if (isMobile) {
            setNoToolbar(true);
        } else {
            const noToolbarParam = query.get('noToolbar');
            const forcedOff = noToolbarParam != null && ['true', '1'].includes(decodeURIComponent(noToolbarParam));
            setNoToolbar(forcedOff);
        }
    }, [serverPublic, isMobile]);

    useEffect(() => {
        let tools: (EditorTool | ExtendedEditorToolDefinition)[] = [];
        if (serverPublic) {
            tools = toolbarPublicDef();
        } else if (isIframe) {
            tools = [];
        } else {
            tools = toolbarFullDef(null, null, podporaKolekci);
        }
        setCurrentTools(tools);
    }, [isIframe]);

    // Dopočítané povolené nástroje berou v potaz uživatelská práva na vrstvu,
    // zatimco v mapě se řeší pouze nástroje k dané geometrii
    const [computedToolsEnabled, setComputedToolsEnabled] = useState<string[]>([]);
    const opravneniQuery = useOpravneniQuery(writableLayers, serverPublic, isIframe);

    const opravneniData = opravneniQuery.data;
    const userLayerActions = useMemo(
        () =>
            getUserLayerActions({
                opravneniData,
                writableLayers,
                serverPublic,
                isIframe,
                layersMode,
            }),
        [opravneniData, writableLayers, serverPublic, isIframe, layersMode],
    );

    useEffect(() => {
        const allTypes = flattenTools(currentTools);
        const enabled = computeEnabledTools(
            allTypes,
            toolsEnabled,
            selectedDetailFeature,
            userLayerActions.userActionTypes,
            mapioContext.value.isIframe,
            serverPublic as boolean,
            layersMode,
        );
        // Uložit jen při skutečné změně obsahu – computeEnabledTools vrací pokaždé nové pole,
        // což jinak vede ke zbytečnému re-renderu MapPage i celého map-podstromu.
        setComputedToolsEnabled(prev => {
            const same = prev.length === enabled.length && prev.every((v, i) => v === enabled[i]);
            return same ? prev : enabled;
        });
    }, [
        currentTools,
        selectedDetailFeature,
        serverPublic,
        mapioContext.value.isIframe,
        toolsEnabled,
        currentTopic,
        layersMode,
        userLayerActions,
    ]);

    useEffect(() => {
        if (layersConfigQuery.data) {
            const getDataFromMapioHtml = (event: MessageEvent<any>): void => {
                let receivedEvent = event.data;
                if (typeof receivedEvent === 'string') {
                    try {
                        receivedEvent = JSON.parse(receivedEvent);
                    } catch (e) {
                        console.warn('Nešlo parsovat event jako JSON, zůstává jako string', receivedEvent);
                    }
                }
                // Některé eventy se posílají jako JSON a je třeba je parsovat
                if (typeof receivedEvent.event === 'string') {
                    receivedEvent.event = JSON.parse(receivedEvent.event);
                }
                // Eventy z mapio-react-client chodí v jiném formátu než zde potřebujeme
                // {type: "", sender: "" event: {potřebujeme}}
                if (receivedEvent.event) {
                    receivedEvent = receivedEvent.event;
                }
                const {
                    features,
                    zoom,
                    zoomPadding,
                    zoomRatio,
                    zoomMinScale,
                    useAsOrigin,
                    merge,
                    featurePointers,
                    featurePointer,
                    featureIdType,
                    loadMissing,
                    showDetail,
                    layerId,
                    layerIds,
                    featureIds,
                    id,
                } = receivedEvent;
                switch (receivedEvent.type) {
                    case 'READY':
                        if (mapioContext.value.isIframe) {
                            sendReadyEvent(mapioContext.value.instanceId);
                        }
                        break;
                    case 'FEATURE_LOAD':
                    case 'FEATURE_TO_LAYER_LOAD':
                        ref.current?.loadFeatures(features, {zoom, padding: zoomPadding, zoomRatio, zoomMinScale, useAsOrigin, merge}, layerId);
                        break;
                    case 'FEATURE_SELECT':
                        ref.current.selectFeatures(featurePointers, {
                            featureIdType,
                            loadMissing,
                            showDetail,
                            zoom,
                            padding: zoomPadding,
                            zoomRatio,
                            zoomMinScale,
                            useAsOrigin,
                        });
                        break;
                    case 'FEATURE_HIGHLIGHT':
                        ref.current?.highlightFeature(featurePointer, receivedEvent.hover ?? false);
                        break;
                    case 'FEATURE_ZOOM':
                        ref.current?.zoomOnFeatures(featurePointers, {
                            featureIdType,
                            loadMissing,
                            padding: zoomPadding,
                            zoomRatio,
                            zoomMinScale,
                            useAsOrigin,
                        });
                        break;
                    case 'FEATURE_CENTER':
                        ref.current.centerOnFeatures(featurePointers, {featureIdType, loadMissing, useAsOrigin, padding: zoomPadding});
                        break;
                    case 'FEATURE_DELETE':
                        ref.current.deleteFeatures(featurePointers);
                        break;
                    case 'FEATURE_UNLOAD':
                        ref.current.unloadFeatures(featureIds);
                        break;
                    case 'LAYER_ZOOM':
                        ref.current.zoomOnLayer(id, {padding: zoomPadding, zoomRatio, zoomMinScale});
                        break;
                    case 'TOOL_ACTIVATION':
                        ref.current.activateToolWithType(receivedEvent.toolType);
                        break;
                    case 'TOOL_ACTIVATED':
                        if (receivedEvent.toolType === EditorToolType.SNAP) {
                            setSnappingActive(true);
                            return;
                        }
                        setActiveTool(receivedEvent.toolType);
                        break;
                    case 'MEASURE_FEATURES':
                        ref.current.measureFeatures(layerIds, features);
                        break;
                    case 'PROPERTY_SET':
                        switch (receivedEvent.name) {
                            case 'topic':
                                // Změna topicu - vše se řeší v centralizovaném useEffect(topicId)
                                ref.current.setTopic(receivedEvent.value);
                                break;
                            case 'zoom':
                                ref.current.setZoom(receivedEvent.value);
                                break;
                            case 'tools':
                                setCurrentTools(addEditTools(receivedEvent.value, selectedDetailFeature, layersConfigQuery.data, serverPublic, podporaKolekci));
                                // Chceme umožnit nástroje zobrazit,
                                // pouze pokud není deklarováno v URL, to má prioritu
                                if (!query.get('noToolbar')) setNoToolbar(false);
                                break;
                            case 'contextTools':
                                setContextTools(receivedEvent.value);
                                break;
                            case 'layerSettingsEnabled':
                                mapioContext.setLayerSettingsEnabled(receivedEvent.value);
                                break;
                            case 'layersMode':
                                setLayersMode(receivedEvent.value);
                                break;
                            case 'layers':
                                // Explicitní nastavení vrstev z eventu (má přednost před automatickým nastavením)
                                setActiveLayers(receivedEvent.value);
                                ref.current.setLayers(receivedEvent.value);
                                break;
                            case 'setContextParams':
                                mapioContext.set({...mapioContext.value, context: updateContext(mapioContext.value.context, receivedEvent.value)});
                                break;
                            case 'catalogMenuVisible':
                                setIsCatalogMenuVisible(receivedEvent.value);
                                break;
                            case 'featureTooltips':
                                setFeatureTooltips(receivedEvent.value);
                                break;
                            case 'dateFilterItems':
                                setDateFilterItems(receivedEvent.value);
                                break;
                            case 'setInsertionLayer':
                                ref.current.setInsertionLayer(receivedEvent.value);
                                break;
                            case 'updateTopic':
                                ref.current.updateTopic(receivedEvent.value);
                                break;
                            case 'color':
                                setColor(receivedEvent.value);
                                break;
                            default:
                                console.warn('[MapPage] Nepodporovaná property pro event PROPERTY_SET', receivedEvent.name, {receivedEvent});
                        }
                        break;
                    case 'PROPERTY_GET':
                        break;
                    case 'ACTION':
                        switch (receivedEvent.actionId) {
                            case ActionTypes.DISTANCE_CHANGED:
                                setDistanceData({
                                    currentDistance: receivedEvent.currentDistance,
                                    currentPixelDistance: receivedEvent.currentPixelDistance,
                                    currentZoomLevel: receivedEvent.currentZoomLevel,
                                });
                                break;
                        }
                        break;
                }
            };

            if (isReactNative) {
                document.addEventListener('message', getDataFromMapioHtml);
            } else {
                window.addEventListener('message', getDataFromMapioHtml);
            }
            return (): void => {
                if (isReactNative) {
                    document.removeEventListener('message', getDataFromMapioHtml);
                } else {
                    window.removeEventListener('message', getDataFromMapioHtml);
                }
            };
        }
    }, [layersConfigQuery.data]);

    useEffect((): void => {
        if (activeTool !== EditorToolType.SHOW_COORDS) {
            setCoordinateLocation(null);
        }
        setOtherToolsTrigger({...otherToolsTrigger, toolInfo: null});
    }, [activeTool]);

    /** Nástroje zobrazující se v levém menu */
    useEffect(() => {
        let tools: (EditorTool | ExtendedEditorToolDefinition)[] = [];
        tools.push(...currentTools);

        // Chceme, aby při aktivním nástroji kopírování byl pořád zobrazen panel s akčními nástroji
        if (activeTool !== EditorToolType.COPY) {
            tools = addEditTools(tools, selectedDetailFeature, layersConfigQuery?.data, serverPublic, podporaKolekci);
            setCurrentTools(tools);
            ref?.current?.setTools(filterToolsByEnabled(tools, computedToolsEnabled, insertionLayerId, layersConfigQuery?.data) as typeof currentTools);
        }
    }, [isTempEditingStarted, selectedDetailFeature, layersConfigQuery?.data, insertionLayerId]);

    /*** Mazání features
     * @params layerId, featureId
     */
    const deleteFeatureHandler = async (layerId, featureId, updating = true) => {
        const featurePointers = [{layerId, featureId}];
        if (ref.current) {
            ref.current.deleteFeatures(featurePointers);
            if (updating) {
                if (isAttributeTableOpen) attrFinalTableData.refetch();
                await UserLayersApi.deleteLayerFeature(layerId, featureId);
            }
        } else {
            console.error('MapEditorActions not found');
        }
    };

    /*** Mazání features
     * @params featurePointers : [{layerId, featureId}]
     */
    const deleteMultipleFeaturesHandler = async (featurePointers: FeaturePointer[], updating = true, invalidate?: () => void) => {
        if (ref.current) {
            if (!mapioContext.value.isIframe && updating && layersMode !== EditorLayersMode.OVERLAY && !featurePointers.some(fp => fp.partIndex)) {
                // Rozřazení featurePointers podle layerId
                const featuresByLayer = featurePointers.reduce((acc, feature) => {
                    const {layerId, featureId} = feature;
                    if (!acc[layerId]) {
                        acc[layerId] = [];
                    }
                    acc[layerId].push(featureId);
                    return acc;
                }, {});

                // Vytvoření hromadných mazacích požadavků
                const deletePromises = Object.entries(featuresByLayer).map(([layerId, featureIds]) =>
                    UserLayersApi.deleteLayerFeatureBatch(Number(layerId), featureIds as string[]),
                );

                await Modal.showWaiting(
                    actionApiCall(async (): Promise<any> => await Promise.all(deletePromises)),
                    ApiCallWaitingModal,
                );

                // Invalidace cache
                await queryClient.invalidateQueries([CACHE_FEATURES_IDS, ...Object.keys(featuresByLayer)]);
                if (isAttributeTableOpen) attrFinalTableData.refetch();
            }
            ref.current.deleteFeatures(featurePointers);
            if (invalidate) {
                invalidate();
            }
        }
    };

    /**
     * Smazání featury s atributy
     * @params featurePointers: FeaturePointer[]
     */
    const handleDeleteAttributeForm = async (featurePointers: FeaturePointer[], invalidate?: () => void) => {
        if (featurePointers?.length < 1) return;

        const layerIds = [...new Set(featurePointers.map(fp => getEffectiveLayerId(layersConfigQuery?.data, fp.layerId)))];
        if (!canDeleteFeatures(layerIds, userLayerActions.userActionTypes, serverPublic, mapioContext.value?.isIframe)) {
            toaster.errorWithTitle('Nedostatečná oprávnění', 'Nemáte právo odstranit vybrané objekty');
            return;
        }

        const result = await showConfirm({
            title: 'Odstranit objekt',
            message: 'Opravdu chcete smazat objekt?',
            intent: 'danger',
        });
        if (result) {
            await deleteMultipleFeaturesHandler(featurePointers, true, invalidate);
        }
    };

    const handleAddExisting = async (event: EditorUpdateEvent) => {
        const {features, originFeaturePointer} = event;
        // Podpora hromadného batch zpracování (Shift+klik workflow)
        const pointers: (typeof originFeaturePointer)[] = (event as any).originFeaturePointers ?? (originFeaturePointer ? [originFeaturePointer] : []);

        if (!features?.length || !pointers.length) {
            const layerIds = [...new Set(pointers.map(p => p?.layerId).filter(Boolean))];
            layerIds.forEach(layerId => ref.current.reloadLayer(layerId));
            return;
        }

        const isBatch = pointers.length > 1;
        const message = isBatch ? (
            <span>
                Přidáním {pointers.length} objektů do kolekce zaniknou jejich metadata.
                <br /> Opravdu chcete pokračovat?
            </span>
        ) : (
            <span>
                Přidáním objektu do kolekce zaniknou metadata objektu s id: <strong>{pointers[0].featureId}</strong>.<br /> Opravdu chcete pokračovat?
            </span>
        );

        const result = await showConfirm({
            title: isBatch ? `Přidat existující objekty (${pointers.length})` : 'Přidat existující objekt',
            message,
        });

        const reloadAll = async () => {
            const layerIds = [...new Set(pointers.map(p => p.layerId))];
            await Promise.all(
                layerIds.map(layerId => {
                    const featureIds = pointers.filter(p => p.layerId === layerId).map(p => p.featureId);
                    return ref.current.reloadLayer(layerId, [...featureIds, features?.[0].id]);
                }),
            );
        };

        if (result) {
            await handleAttributesForm(
                features?.[0],
                [],
                features?.[0]?.properties,
                true,
                null,
                async () => {
                    await deleteMultipleFeaturesHandler(pointers);
                    await reloadAll();
                },
                async () => {
                    await reloadAll();
                },
            );
        } else {
            await reloadAll();
        }
        ref.current.selectFeatures([{layerId: features?.[0].properties.layerId, featureId: features?.[0].id}]);
    };

    /**
     * Přesun na koordinační body featury
     * @params layerId, featureId
     */
    const moveToFeatureHandler = (featurePointers: FeaturePointer[]) => {
        if (ref.current) {
            // Pokud je atributová tabulka otevřená, přidáme její výšku jako bottom padding
            // aby se feature vycentrovala do viditelné části mapy nad tabulkou.
            // DOM query je záměrně zde v parent okně (ne v iframe mapio-react-client).
            const attrTableHeight = document.querySelector('.att_table_container')?.getBoundingClientRect().height ?? 0;
            const padding: [number, number, number, number] = [0, 0, attrTableHeight, 0];
            if (isSelectTool(activeTool)) {
                ref.current.selectFeatures(featurePointers, {zoom: FeatureZoom.CENTER, padding});
            } else {
                ref.current.centerOnFeatures(featurePointers, {padding});
            }
            // Aktualizujeme vše ve stejném renderu aby Sidebar nedostal nekonzistentní props
            setSelectedDetailFeature(featurePointers);
            setDetailLayerId(featurePointers[0]?.layerId ?? null);
            setDetailFeatureId(featurePointers[0]?.featureId ?? '');
        }
    };

    /**
     * Zobrazení dialogového formuláře atributů featury
     * @params feature, layerAttributes, initialValues, update
     */
    const handleAttributesForm = async (
        feature,
        layerAttributes: LayerAttributeVO[] = [],
        initialValues = [] as any,
        update = true,
        actionId?: number,
        invalidate?: (featureId?: FeatureId) => void,
        onCancel?: () => void,
    ): Promise<void> => {
        // Vrstvu objektu doplňuje engine do properties jen v režimu vrstev DATA. U nového objektu ji jinak
        // známe z toolbaru: výběr "Zakreslit" plní insertionLayerId, zatímco checkedLayerId nese jen
        // zaškrtnutí vrstvy k editaci v panelu vrstev. Bez insertionLayerId zůstávalo layerId prázdné,
        // takže se nenačetla ani práva, ani atributy a formulář se otevřel bez atributů (MCW-29258).
        const layerId = feature?.properties?.layerId ?? (update ? checkedLayerId : (insertionLayerId ?? checkedLayerId));
        if (layerId === OVERLAY_LAYER_ID) return;
        if (layerId == null) {
            // Bez vrstvy nejde objekt uložit – srozumitelná hláška je lepší než formulář bez atributů.
            toaster.error('Nepodařilo se určit vrstvu objektu. Vyberte vrstvu pro zákres a akci zopakujte.');
            onCancel?.();
            return;
        }
        // Získáme akce dostupné uživateli
        const getUserActions = await actionApiCall(async (): Promise<UserRightsVO[]> => (await RightsApi.getAllLayerRightsForLoggedInUser([layerId])).data);
        let images = [];
        let documents = [];

        const layerRights = getUserActions?.[0]?.layers[0];
        const canEditAllAttributes = update && Boolean(layerRights?.vlastnikVrstvy || layerRights?.actionRights?.allActions);

        if (getUserActions && !actionId && !canEditAllAttributes) {
            const userActions = layerRights?.actionRights.actionList;
            const actionType = update ? 'UPDATE' : 'INSERT';
            const userSpecificActions = userActions?.filter(item => item.typ === actionType);
            const defaultAction = userSpecificActions?.find(item => item.vychozi === true);

            if (defaultAction && layerRights?.actionRights.allActions) {
                actionId = defaultAction.id;
            } else {
                const firstAction = userSpecificActions?.[0];
                if (firstAction) {
                    actionId = firstAction.id;
                }
            }
        }
        if (actionId && layerId) {
            layerAttributes = await Modal.showWaiting<LayerAttributeVO[]>(
                actionApiCall(async (): Promise<LayerAttributeVO[]> => (await AkceApi.getActionAttributes(actionId)).data),
                ApiCallWaitingModal,
            );
        }
        if (update) {
            const [imagesResult, documentsResult] = await Modal.showWaiting(
                Promise.all([
                    actionApiCall(async (): Promise<number[]> => (await DocumentApi.findImagesByData(feature.id)).data),
                    actionApiCall(async (): Promise<DocumentVO[]> => (await DocumentApi.findDocumentsByData(feature.id)).data),
                ]),
                ApiCallWaitingModal,
            );

            images = imagesResult;
            documents = documentsResult.map((doc: DocumentVO) => ({
                id: doc.id,
                popis: doc.popis,
                nazev: doc.nazev,
                kodTypDokument: doc.typDokumentu?.kod,
            }));
        }
        //  else {
        // Pokud uživatel nemá přiřazenou insertí akci a je vlastníkem vrstvy, tak hledáme všechny atributy.
        // jestli uživatel není vlastníkem a nemá žádnou insertí akci, tak se ani formulář nevyvolá
        if (layerAttributes.length === 0 && layerId && (canEditAllAttributes || layerRights?.vlastnikVrstvy === true)) {
            layerAttributes = await Modal.showWaiting<LayerAttributeVO[]>(
                actionApiCall(async (): Promise<LayerAttributeVO[]> => (await UserLayersApi.getLayerAttributes(layerId, mapioContext.value.context)).data),
                ApiCallWaitingModal,
            );
        }
        // }

        Modal.show(({onClose}) => (
            <AttributesModal
                featureId={feature.id}
                layerId={layerId}
                title={`${feature.properties?.label ?? 'Nevyplněno'}`}
                btnTitle={update ? 'Upravit' : 'Vytvořit'}
                attributes={layerAttributes}
                onClose={() => {
                    onClose();
                    // Pokud uživatel zruší formulář, smažeme vytvořenou featuru z mapy (ze stejné vrstvy,
                    // do které se zakreslovala – viz odvození layerId výše)
                    if (feature.id && feature.properties && !update) {
                        deleteFeatureHandler(layerId, feature.id, false);
                    }
                    if (onCancel) {
                        onCancel(); // Zde zavoláme callback onCancel
                    }
                    focusMapIframe();
                }}
                isEdit={update}
                initialValues={{...initialValues, tmpImages: images, tmpDocs: documents}}
                onSubmit={async (data: LayerAttributeVO) => {
                    let mutateBoolean = booleanToString(data);

                    //nechápu jak někomu tento formulář může fungovat, protože neposílají data ale nějaká externí data, která se magicky mění při změně ve formulář... ale samozřejmě to nefunguje pokud se properta komplet odmaže...
                    //tldr tuten formulář je celý na přepsání, ale nemám kapacitu tak jenom opravím problém se stagnantními daty při smazání
                    feature.properties = removeUncontained(feature.properties, data);

                    feature.properties = {...feature.properties, ...mutateBoolean};
                    return await rffFormSubmit(async () => {
                        // todo začistit, hroznej chaoos
                        const tmpImages: any[] = feature.properties.tmpImages;
                        const tmpDocs: any[] = feature.properties.tmpDocs;
                        const filteredTmpDocs = tmpDocs.map(({nazev, ...rest}) => rest);
                        const documentIds = documents.map(doc => doc.id);
                        const updatedTmpDocs = filteredTmpDocs.map(doc => {
                            if (documentIds.includes(Number(doc.id))) {
                                return {...doc, id: Number(doc.id)};
                            }
                            return doc;
                        });
                        const filteredTmpDocsIds = filteredTmpDocs.map(doc => Number(doc.id));

                        delete feature.properties.tmpImages;
                        delete feature.properties.tmpDocs;
                        const submitData = {
                            feature: feature,
                            tmpImages: tmpImages.filter(item => typeof item !== 'number'),
                            deleteImageIds: images.filter(item => !tmpImages.includes(item)),
                            tmpDocs: updatedTmpDocs,
                            deleteDocIds: documentIds.filter(doc => !filteredTmpDocsIds.includes(doc)),
                        };

                        let featureId = feature.id;

                        if (update) {
                            await actionApiCall(async () => await UserLayersApi.updateLayerFeature(layerId, actionId, submitData));
                        } else {
                            const newFeatureId = await actionApiCall(async () => (await UserLayersApi.saveLayerFeature(layerId, actionId, submitData)).data);
                            if (newFeatureId) {
                                featureId = newFeatureId;
                            }
                        }

                        ref.current.reloadDetail(featureId, feature.properties?.parentLayerId ?? layerId);
                        await queryClient.invalidateQueries([CACHE_KEY_FEATURE_DETAIL, JSON.stringify(selectedDetailFeature)]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_DETAIL, detailLayerId, detailFeatureId]);
                        await queryClient.invalidateQueries([CACHE_FEATURES_IDS, feature.properties?.parentLayerId ?? layerId]);

                        if (isAttributeTableOpen) {
                            if (!update) {
                                await queryClient.invalidateQueries([CACHE_FEATURES_IDS, feature.properties?.parentLayerId ?? layerId], {
                                    refetchActive: true,
                                    refetchInactive: false,
                                });
                            }
                            if (update) {
                                attrFinalTableData.invalidateDataRows([featureId]);
                            } else {
                                attrFinalTableData.insertRows([featureId]);
                            }
                            moveToFeatureHandler([{layerId: feature.properties?.parentLayerId ?? layerId, featureId}]);
                        }
                        ref.current.reloadLayer(feature.properties?.parentLayerId ?? layerId, [featureId]);
                        if (invalidate) {
                            invalidate(featureId);
                        }
                        onClose();
                        // Fokus zpět do mapy, aby fungovaly klávesové zkratky enginu (např. 2x Esc)
                        focusMapIframe();
                    });
                }}
            />
        ));
    };

    /**
     * Update feature po akci v mapě bez vyvolání dialogového okna
     * @param feature
     * @param showDialog
     */
    const handleUpdateFeature = async (features: any[], showDialog = true): Promise<void> => {
        // Dávkové ukládání (jeden request na vrstvu) žije v helpers/featureUpdates (viz updateFeaturesBatched).
        if (showDialog) {
            // Jeden čekací modal pro celou dávku (ne jeden per featura). U hromadné akce ukážeme počet.
            const title = features.length > 1 ? `Probíhá ukládání objektů (${features.length})` : undefined;
            await Modal.showWaiting(updateFeaturesBatched(features), props => <ApiCallWaitingModal {...props} title={title} />);
        } else {
            await updateFeaturesBatched(features);
        }
    };

    /**
     * Zaznamená krok do historie "Zpět". Volá se po dokončení editace geometrie.
     * Reaguje pouze na nástroje Editace/Posuv/Otočení/Offset a jen když engine přiložil stav před změnou
     * (`previousFeatures`). Nový krok vymaže frontu "Vpřed" (redo). Historie je omezena na {@link MAX_UNDO_STEPS} kroků.
     */
    const recordGeometryUndoStep = useCallback((event: EditorUpdateEvent): void => {
        const label = UNDO_TOOL_LABELS[event.toolType];
        const beforeFeatures = event.previousFeatures;
        const afterFeatures = event.features;
        if (!label || !beforeFeatures?.length || !afterFeatures?.length) {
            return;
        }
        setUndoStack(prev => {
            const step: GeometryUndoStep = {
                id: `${event.toolType}-${Date.now()}-${prev.length}`,
                toolType: event.toolType,
                label,
                beforeFeatures,
                afterFeatures,
                timestamp: Date.now(),
            };
            return [...prev, step].slice(-MAX_UNDO_STEPS);
        });
        setRedoStack([]);
    }, []);

    /**
     * Uloží zadané featury na server a obnoví jejich zobrazení v mapě (a detail, je-li otevřený).
     * Sdílené jádro pro "Zpět" i "Vpřed" – engine drží jinou geometrii, proto ji po uložení znovu načteme.
     */
    const applyGeometryFeatures = useCallback(
        async (features: Feature[]): Promise<void> => {
            if (!features.length) {
                return;
            }
            await handleUpdateFeature(features);
            const selectedKeys = new Set((selectedDetailFeature ?? []).map(p => `${p.layerId}:${p.featureId}`));
            const reloaded = new Set<string>();
            for (const f of features) {
                const layerId = f.properties?.parentLayerId ?? f.properties?.layerId;
                if (layerId == null) continue;
                const key = `${layerId}:${f.id}`;
                if (reloaded.has(key)) continue;
                reloaded.add(key);
                ref.current?.reloadLayer(layerId, [f.id]);
                // Detail obnovíme jen pokud je daná feature právě otevřená v detailu.
                if (selectedKeys.has(key)) {
                    ref.current?.reloadDetail(f.id, layerId);
                }
            }
        },
        [selectedDetailFeature], // eslint-disable-line react-hooks/exhaustive-deps
    );

    /**
     * Vrátí historii chronologicky zpět až do stavu PŘED krokem na daném indexu (včetně).
     * Pro každou dotčenou feature se použije její stav z NEJSTARŠÍHO vraceného kroku (cílový stav).
     * Vrácené kroky se přesunou do fronty "Vpřed" (redo) tak, aby šly znovu provést v původním pořadí.
     */
    const undoToStep = useCallback(
        async (targetIndex: number): Promise<void> => {
            if (targetIndex < 0 || targetIndex >= undoStack.length) {
                return;
            }
            const reverted = undoStack.slice(targetIndex);
            // Klíč = id feature; první výskyt (od nejstaršího kroku) = cílový stav, do kterého se vracíme.
            const byId = new Map<string | number, Feature>();
            for (const step of reverted) {
                for (const pf of step.beforeFeatures) {
                    if (pf.id != null && !byId.has(pf.id)) {
                        byId.set(pf.id, pf);
                    }
                }
            }
            setUndoStack(prev => prev.slice(0, targetIndex));
            // Do redo fronty přidáme vrácené kroky tak, aby "Vpřed" re-aplikoval nejstarší z nich jako první.
            setRedoStack(prev => [...prev, ...reverted.slice().reverse()]);
            await applyGeometryFeatures(Array.from(byId.values()));
        },
        [undoStack, applyGeometryFeatures],
    );

    /**
     * Provede znovu poslední vrácený krok ("Vpřed" / redo) – nastaví geometrii zpět na stav PO změně
     * a krok vrátí do historie "Zpět".
     */
    const redoLastStep = useCallback(async (): Promise<void> => {
        if (redoStack.length === 0) {
            return;
        }
        const step = redoStack[redoStack.length - 1];
        setRedoStack(prev => prev.slice(0, -1));
        setUndoStack(prev => [...prev, step].slice(-MAX_UNDO_STEPS));
        await applyGeometryFeatures(step.afterFeatures);
    }, [redoStack, applyGeometryFeatures]);

    // Ref s aktuálními akcemi historie – používá se z window keydown listeneru i z CUSTOM_ACTION z enginu,
    // aby se předešlo stale closure a přegenerování listeneru při každé změně zásobníku.
    const geometryHistoryRef = useRef<{undo: () => void; redo: () => void}>({undo: () => {}, redo: () => {}});
    geometryHistoryRef.current = {
        undo: () => {
            if (undoStack.length > 0) void undoToStep(undoStack.length - 1);
        },
        redo: () => {
            void redoLastStep();
        },
    };

    // Klávesové zkratky Ctrl+Z / Ctrl+Y (Ctrl+Shift+Z) pro fokus mimo mapu; nad mapou je přeposílá engine (viz CUSTOM_ACTION).
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (!e.ctrlKey) return;
            const target = e.target as HTMLElement | null;
            const tag = target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
            const key = e.key.toLowerCase();
            if (key === 'z' && !e.shiftKey) {
                e.preventDefault();
                geometryHistoryRef.current.undo();
            } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
                e.preventDefault();
                geometryHistoryRef.current.redo();
            }
        };
        globalThis.addEventListener('keydown', onKeyDown);
        return () => globalThis.removeEventListener('keydown', onKeyDown);
    }, []);

    const handleCreateMultipleFeatures = async (features: any[], showDialog: false): Promise<void> => {
        const wrappers: FeatureWrapperVO[] = features.map(feature => ({feature: {...feature, properties: simplifyProperties(feature.properties)}}));

        const createPromises = wrappers.map(wrapper => UserLayersApi.saveLayerFeature(Number(wrapper.feature.properties.layerId), null, wrapper));

        let newFeatureResponses;
        if (showDialog) {
            newFeatureResponses = await Modal.showWaiting(
                actionApiCall(async () => await Promise.all(createPromises)),
                ApiCallWaitingModal,
            );
        } else {
            newFeatureResponses = await actionApiCall(async () => await Promise.all(createPromises));
        }

        const newFeatureIds = newFeatureResponses.map(response => response.data);
        ref.current.reloadLayer(features[0].properties?.parentLayerId ?? features[0].properties.layerId, newFeatureIds);
    };

    /**
     * Nastaví do statu všechny vrstvy, nad kterými jsou povolené editační operace
     */
    // Drží aktuální writableLayers pro porovnání v onWritableLayersChange bez stale closure / změny identity callbacku
    const writableLayersRef = useRef<LayerId[]>(writableLayers);
    writableLayersRef.current = writableLayers;
    const onWritableLayersChange = useCallback(
        (layers: number[]): void => {
            if (!layersConfigQuery.isFetched) return;
            const availableLayerIds = new Set(Object.keys(layersConfigQuery.data).map(Number));
            // Filtrace vrstev, aby byly zahrnuty pouze ty, které se nacházejí v configu
            // protože do mapy můžeme přidat externí vrstvy a ty nechceme, aby byly editovatelné
            const filteredLayers = layers.filter(layer => availableLayerIds.has(layer));
            // Porovnání s aktuálním stavem. Bez této kontroly by setWritableLayers vždy vytvořil nové pole
            // i objekt contextu → re-render → mapa přes postMessage znovu vyvolá onWritableLayersChange →
            // echo-smyčka končící "Too many re-renders". Ukládáme jen při skutečné změně obsahu.
            const current = writableLayersRef.current ?? [];
            const isSame = filteredLayers.length === current.length && filteredLayers.every(id => current.includes(id));
            if (!isSame) {
                setWritableLayers(filteredLayers);
            }
        },
        [layersConfigQuery.data, layersConfigQuery.isFetched],
    );

    /**
     * Mapový klient hlásí, jaké téma má skutečně nastavené (může se lišit od požadovaného, když ho
     * ve své konfiguraci nenajde). Držíme se toho, aby se obálka a mapa nemohly rozejít.
     */
    const onTopicChange = useCallback((changedTopicId: string): void => {
        // Když přepnutí vyvolala obálka, je to jen ozvěna již známého stavu.
        if (!changedTopicId || syncedTopicIdRef.current === changedTopicId) {
            return;
        }
        syncedTopicIdRef.current = changedTopicId;
        mapioContext.setTopicId(changedTopicId);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Nastaví aktivní podkladovou vrstvu
     */
    const onBackgroundLayerChange = useCallback((layerId: LayerId): void => {
        setBackgroundLayer(layerId);
    }, []);

    /**
     * Nastaví aktivní podkladovou vrstvu
     */
    const onForegroundLayerChange = useCallback((layerId: LayerId): void => {
        setForegroundLayer(layerId);
    }, []);

    /**
     * Nastaví aktivní vrstvu
     */
    const onLayersChange = useCallback(
        (layers: LayerId[] | {layerId: LayerId; label: string}[]): void => {
            // Z react klienta přijde jako objekt s layerId a label, ale z Angularu pouze jako list ids
            let layerIds: LayerId[];
            if (typeof layers[0] === 'number') {
                layerIds = layers as LayerId[];
            } else {
                layerIds = (layers as {layerId: LayerId; label: string}[]).map(layer => layer.layerId);
            }
            setActiveLayers(layerIds);
            if (layerIds.length === 0) {
                setIsAttributeTableOpen(false);
            } else {
                // Pokud byla vypnuta vrstva zobrazená v tabulce (selectedLayerId nebo activeFilterLayerId),
                // přepneme tabulku na první stále dostupnou vrstvu, nebo ji zavřeme
                if (selectedLayerId && !layerIds.includes(selectedLayerId)) {
                    const fallback = layerIds[0] ?? null;
                    setSelectedLayerId(fallback);
                    setActiveFilterLayerId(fallback);
                    if (!fallback) {
                        setIsAttributeTableOpen(false);
                    }
                }
            }
        },
        [selectedLayerId, setActiveLayers, setActiveFilterLayerId, setIsAttributeTableOpen, setSelectedLayerId],
    );

    const handleOpenImageGallery = useCallback((imageEvent: any) => {
        if (imageEvent.data) {
            setImageEventData(imageEvent);
            setIsImageGalleryOpen(true);
        }
    }, []);

    /**
     * Zpracování akce zobrazení formuláře atributu z detailu
     */
    const handleProcessDetailAction = async (layerId: LayerId, featureId: FeatureId): Promise<void> => {
        const attributes: LayerAttributeVO[] = await actionApiCall(
            async (): Promise<LayerAttributeVO[]> => (await UserLayersApi.getLayerAttributes(Number(layerId), mapioContext.value?.context)).data,
        );
        const detail: LayerDetailVO = await actionApiCall(
            async (): Promise<LayerDetailVO> =>
                (await UserLayersApi.getLayerDetail(Number(layerId), featureId?.toString(), undefined, mapioContext.value?.context)).data,
        );

        if (detail) {
            detail.properties = {...detail.properties, layerId} as LayerDetailVO['properties'];
        }
        await handleAttributesForm(detail, attributes, detail.properties, true);
    };

    const handleAttributesFormAction = async (layerId: LayerId, featureId: FeatureId, actionId: any, label: string): Promise<void> => {
        const attributes: LayerAttributeVO[] = await actionApiCall(async (): Promise<LayerAttributeVO[]> => (await AkceApi.getActionAttributes(actionId)).data);
        const detail = await actionApiCall(
            async (): Promise<LayerDetailVO> => (await UserLayersApi.getLayerDetail(Number(layerId), featureId?.toString())).data,
        );
        if (detail) {
            let processedProps = flattenEnumObject(detail.properties);
            detail.properties = processedProps;
        }
        await handleAttributesForm(detail, attributes, {...detail.properties, actionLabel: label}, true, actionId);
    };

    /**
     * Modal pro zpracování eventu vyvolaného pro modal nástroje LINE_OFFSET
     */
    const handleLineOffsetModal = async (writableFeaturePointers, options?: any) => {
        ref.current.selectFeatures(writableFeaturePointers);
        Modal.show(({onClose}) => (
            <LineOffsetModal
                title={'Vytvoření linie s odsazením'}
                targetLayerOptions={options?.candidateInsertionLayers}
                onClose={onClose}
                onSubmit={async data => {
                    return await rffFormSubmit(async () => {
                        const {offset, targetLayerId} = data;
                        if (targetLayerId) {
                            ref.current.setInsertionLayer(targetLayerId);
                        }
                        ref.current.sendToolEvent(EditorToolType.LINE_OFFSET, {offset});
                        onClose();
                    });
                }}
            />
        ));
    };

    const handlePerpendicularLineModal = async (maxOffset, feature) => {
        Modal.show(({onClose}) => (
            <PerpendicularLineModal
                title={'Vytvoření kolmice'}
                onClose={onClose}
                onSubmit={async data => {
                    return await rffFormSubmit(async () => {
                        const {offset, length} = data;
                        ref.current.sendToolEvent(EditorToolType.PERPENDICULAR_LINE, {feature, offset, length});
                        onClose();
                    });
                }}
                maxOffset={maxOffset}
            />
        ));
    };

    const handleMultipleFeaturesSelection = async (featurePointers: FeatureMultipleSelector[], totalCount?: number, addToSelection = false) => {
        Modal.show(({onClose}) => (
            <MultipleFeaturesSelection
                title={'Výběr objektů'}
                onClose={onClose}
                onClick={featurePointer => {
                    // V režimu přidávání (shift+klik) výběr nenahrazujeme – selectedDetailFeature
                    // dopočítá SELECT event z mapy (celý spojený výběr), proto detail neresetujeme.
                    if (!addToSelection && featurePointer.layerId !== OVERLAY_LAYER_ID) {
                        setDetailLayerId(featurePointer.layerId);
                        setDetailFeatureId(featurePointer.featureId);
                        setSelectedDetailFeature([featurePointer]);
                    }
                    const featureIdType = featurePointer.groupId ? FeatureIdType.GROUP_ID : FeatureIdType.FEATURE_ID;
                    ref.current.selectFeatures([featurePointer], {featureIdType, addToSelection});
                }}
                featurePointers={featurePointers}
                totalCount={totalCount}
            />
        ));
    };

    const [otherToolsTrigger, setOtherToolsTrigger] = useState<Trigger>({
        actionToTrigger: ETriggerAction.NONE,
    });

    /*** Zpracování události z komponenty*/
    const handleFeatureEditorEvent = useCallback(
        async (event: EditorEvent): Promise<void> => {
            // Pokud chceme zobrazit galerii například ze šablony angr_detail.ftl
            if (event.type === EditorEventType.CUSTOM_ACTION && event.actionId === 'SHOW_IMAGE') {
                handleOpenImageGallery(event);
                return;
            }
            if (EditorEventType.TEMP_EDITING_STARTED === event.type) {
                setIsTempEditingStarted(true);
            } else if (EditorEventType.TEMP_EDITING_FINISHED === event.type) {
                setIsTempEditingStarted(false);
            }
            // Pokud jsme v iframe, pouze vypropagujeme event dál, logiku si řeší nadřazená aplikace
            if (mapioContext.value.isIframe) {
                sendEvent(event, mapioContext.value.instanceId);
                // přepnuto na switch pro lepší typování
                switch (event.type) {
                    case EditorEventType.SELECT: {
                        const selectEvent = event;
                        let combinedFeatures = [...selectEvent.writableFeaturePointers, ...selectEvent.readOnlyFeaturePointers];
                        combinedFeatures = combinedFeatures.filter((feature, index, self) =>
                            feature.featureId ? self.findIndex(f => f.featureId === feature.featureId) === index : true,
                        );
                        const filteredFeatures = combinedFeatures.filter(feature => feature.layerId !== OVERLAY_LAYER_ID);
                        console.log('<<<< combinedFeatures', combinedFeatures, '<<< filteredFeatures', filteredFeatures);
                        setSelectedDetailFeature(filteredFeatures);
                        break;
                    }
                    case EditorEventType.ACTION: {
                        switch (event.actionId) {
                            case ActionTypes.NETWORK_ERROR:
                                mapioContext.set(prevState => ({
                                    ...prevState,
                                    errors: event.errors,
                                }));
                                break;
                            case ActionTypes.DISTANCE_CHANGED:
                                // Pohyb mapou (změna měřítka/výřezu) → re-aktivace extent filtru pro panel hledání.
                                // I v iframe režimu musí seznam objektů ve výřezu držet krok s mapou.
                                searchExtentRefreshRef.current?.();
                                break;
                            case ActionTypes.MULTIPLE_FEATURES_SELECT:
                                await handleMultipleFeaturesSelection(event.featurePointers, event.totalCount, event.addToSelection);
                                break;
                            case ActionTypes.SHOW_TOASTER:
                                switch (event.intent) {
                                    case 'warning':
                                        toaster.warningWithTitle(event.title, event.message);
                                        break;
                                    case 'error':
                                        toaster.errorWithTitle(event.title, event.message);
                                        break;
                                    case 'success':
                                        toaster.successWithTitle(event.title, event.message);
                                        break;
                                    // case 'info':
                                    //     toaster.infoWithTitle(event.title, event.message);
                                    //     break;
                                }
                        }
                        break;
                    }
                    case EditorEventType.UPDATE: {
                        const updateEvent: EditorUpdateEvent = event;
                        // Potřebujeme zároveň zobrazit sidebary pro měření vzdálenosti či souřadnice (typované)
                        if ([EditorToolType.MEASURE_POLYGON, EditorToolType.MEASURE_LINE].includes(updateEvent.toolType)) {
                            const measurementInfo: MeasurementInfo = updateEvent.measurementInfo;
                            if (measurementInfo) {
                                setOtherToolsTrigger({
                                    actionToTrigger: ETriggerAction.SHOW,
                                    tool: updateEvent.toolType,
                                    toolInfo: measurementInfo,
                                });
                            }
                        }
                        if (updateEvent.toolType === EditorToolType.GEOLOCATION) {
                            const {jtsk, gps} = updateEvent;
                            setCoordinateLocation({jtsk, gps});
                        }

                        break;
                    }
                    case EditorEventType.CUSTOM_ACTION:
                        if (event.toolId === ActionTypes.TOOLS_ENABLED) {
                            setToolsEnabled(event.toolsEnabled);
                        }
                        // Objekty ve výřezu pro panel hledání (public simple view). Panel může běžet
                        // i v iframe/noAppbar režimu, kde se jinak veškerá logika deleguje na nadřazenou
                        // aplikaci – proto EXTENT_FEATURES musíme zpracovat i zde, jinak by seznam
                        // ve výřezu zůstal viset na "Načítám..." (extentFeaturePointers by nikdy nedorazily).
                        if (event.toolId === ActionTypes.EXTENT_FEATURES) {
                            if (event.featureIds !== undefined && event.featureIds !== extentFeatures) {
                                setExtentFeatures(event.featureIds);
                            }
                            if (event.writableFeaturePointers !== undefined) {
                                setExtentFeaturePointers(event.writableFeaturePointers);
                            }
                        }
                        // MeasurementCard je vlastní overlay Mapia – i v iframe režimu ji řídíme lokálně.
                        // Rodičovská aplikace event dostane přes sendEvent výše, ale samotnou kartu
                        // (rozměry, délky hran, zvýraznění) renderuje a spravuje Mapio.
                        if (event.actionId === ActionTypes.GEOMETRY_MEASUREMENT_UPDATE) {
                            setGeometryMeasurementData((event as any).measurementData);
                            setMeasurementFeaturePointers((event as any).featurePointers ?? []);
                            setHighlightedMeasurementEdge(null); // nové měření → zruš zvýraznění hrany
                        }
                        if (event.actionId === 'MEASUREMENT_EDGE_SELECTED') {
                            const partIndex = (event as any).partIndex;
                            setHighlightedMeasurementEdge(partIndex == null ? null : {partIndex, edgeIndex: (event as any).edgeIndex});
                        }
                        break;
                    default:
                        // nic speciálního pro ostatní typy zde neřešíme
                        break;
                }
                return;
            }

            switch (event.type) {
                case EditorEventType.CREATE:
                    {
                        const {feature}: EditorCreateEvent = event;
                        if (isTempEditingStarted) {
                            return; // Probíhá dočasná editace, ignorujeme události
                        }
                        if (event.toolType === EditorToolType.SPLIT_LINE) {
                            return; // Ignorujeme. Pro tento nástroj nás zajíma pouze UPDATE.
                        }
                        if (event.toolType === EditorToolType.REMOVE_FROM_COLLECTION) {
                            const featureToUpdate = event.relatedFeatures;
                            await handleAttributesForm(
                                feature,
                                [],
                                feature.properties,
                                false,
                                null,
                                featureId => {
                                    handleUpdateFeature([featureToUpdate], false);
                                    ref.current.selectFeatures([{layerId: event.originFeaturePointer.layerId, featureId}]);
                                },
                                () => ref.current.reloadLayer(event.originFeaturePointer.layerId, [event.originFeaturePointer.featureId]),
                            );
                            return;
                        }
                        if (feature) {
                            if (event.toolType === EditorToolType.COPY || event.toolType === EditorToolType.PASTE) {
                                const isPaste = event.toolType === EditorToolType.PASTE;
                                feature.properties = {...feature.properties, layerId: event.originFeaturePointer.layerId};
                                handleAttributesForm(feature, [], feature.properties, false, null, featureId => {
                                    if (!isPaste) {
                                        return;
                                    }
                                    ref.current.activateToolWithType(EditorToolType.EXACT_SELECT);
                                    ref.current.selectFeatures([{layerId: feature.properties?.parentLayerId ?? event.originFeaturePointer.layerId, featureId}]);
                                });
                                break;
                            }
                            handleAttributesForm(feature, [], [], false);
                        }
                    }
                    break;

                case EditorEventType.UPDATE:
                    if (isTempEditingStarted) {
                        return; // Probíhá dočasná editace, ignorujeme události
                    }

                    {
                        const {features, toolType} = event;
                        switch (toolType) {
                            case EditorToolType.SPLIT_POLYGON:
                            case EditorToolType.SPLIT_LINE:
                                await handleAttributesForm(
                                    event.relatedFeatures[0],
                                    [],
                                    event.relatedFeatures[0].properties,
                                    true,
                                    null,
                                    () => {
                                        handleCreateMultipleFeatures(features, false);
                                        ref.current.reloadLayer(event.originFeaturePointer.layerId, [event.originFeaturePointer.featureId]);
                                        ref.current.selectFeatures([event.originFeaturePointer]);
                                    },
                                    () => {
                                        ref.current.reloadLayer(event.originFeaturePointer.layerId, [event.originFeaturePointer.featureId]);
                                        ref.current.selectFeatures([event.originFeaturePointer]);
                                    },
                                );
                                sendEvent(event, mapioContext.value.instanceId);
                                break;
                            case EditorToolType.MULTI_ADD_EXISTING:
                                await handleAddExisting(event);
                                break;
                            case EditorToolType.MERGE_LINES: {
                                await handleUpdateFeature(features);
                                // Smazat sloučené featury ze serveru (klientsky jsou již odstraněny v ToolsInteractions)
                                if (event.relatedFeatures?.length > 0) {
                                    const featurePointersToDelete = event.relatedFeatures.map(feature => ({
                                        layerId: feature.properties.layerId,
                                        featureId: feature.id,
                                    }));
                                    deleteMultipleFeaturesHandler(featurePointersToDelete);
                                    sendEvent(event, mapioContext.value.instanceId);
                                }
                                break;
                            }
                            case EditorToolType.LINE_OFFSET:
                            case EditorToolType.LINE_EXTENSION:
                            case EditorToolType.PERPENDICULAR_LINE:
                            case EditorToolType.OFFSET:
                            case EditorToolType.EDIT:
                            case EditorToolType.ROTATE:
                            case EditorToolType.POLYGON:
                            case EditorToolType.POINT:
                            case EditorToolType.LINE:
                            // V případě editace multigeometrií – přidání nové geometrie do kolekce
                            // tvořicími nástroji (souřadnice, kruh, kolmice a rovnoběžky, …)
                            case EditorToolType.LINE_COORDS:
                            case EditorToolType.POLYGON_COORDS:
                            case EditorToolType.POINT_COORDS:
                            case EditorToolType.CIRCLE_COORDS:
                            case EditorToolType.CIRCLE_RADIUS:
                            case EditorToolType.GUIDED_LINE:
                            case EditorToolType.GUIDED_POLYGON:
                            case EditorToolType.FIXED_RECTANGLE:
                            case EditorToolType.ADD_TO_COLLECTION:
                            case EditorToolType.MULTI_FIXED_RECTANGLE:
                            case EditorToolType.COPY:
                            case EditorToolType.CUTOUT_DRAW:
                            case EditorToolType.CUTOUT_REMOVE:
                            case EditorToolType.CUTOUT_FROM_EXISTING:
                            case EditorToolType.CUTOUT_FIXED_RECTANGLE:
                            case EditorToolType.CUTOUT_PASTE:
                                await handleUpdateFeature(features);
                                // Záznam do historie "Zpět" – no-op pro nástroje bez previousFeatures (jen EDIT/MOVE/ROTATE/OFFSET).
                                recordGeometryUndoStep(event);
                                sendEvent(event, mapioContext.value.instanceId);
                                break;
                            case EditorToolType.MOVE: {
                                // Přesun (i velkého množství) objektů: u velkého počtu se nejdřív zeptáme na
                                // potvrzení, samotné uložení pak běží na pozadí (optimisticky – engine už
                                // objekty zobrazuje na novém místě). Detaily viz helpers/featureUpdates.
                                const reloadLayersOf = (toReload: any[]) => affectedLayerIds(toReload).forEach(layerId => ref.current?.reloadLayer(layerId));
                                if (!(await confirmBulkMove(features.length))) {
                                    // Zrušení: server nebyl změněn → obnovit původní geometrii znovunačtením vrstev.
                                    reloadLayersOf(features);
                                    break;
                                }
                                // Při chybě uložení vrátíme neúspěšné featury na původní místo (rollback).
                                persistMovedFeaturesInBackground(features, {onRollback: reloadLayersOf});
                                recordGeometryUndoStep(event);
                                sendEvent(event, mapioContext.value.instanceId);
                                break;
                            }
                            case EditorToolType.MEASURE_LINE:
                            case EditorToolType.MEASURE_POLYGON:
                                {
                                    const measurementInfo: MeasurementInfo = event.measurementInfo;
                                    setOtherToolsTrigger({actionToTrigger: ETriggerAction.SHOW, tool: toolType, toolInfo: measurementInfo});
                                }
                                break;

                            case EditorToolType.GEOLOCATION:
                                {
                                    const {jtsk, gps} = event;
                                    setCoordinateLocation({jtsk, gps});
                                }
                                break;
                            default:
                                console.error(`Nevalidní update toolType event: ${toolType}`);
                        }
                    }
                    break;

                case EditorEventType.SELECT:
                    {
                        let allSelectedFeaturePointers = [...event.readOnlyFeaturePointers, ...event.writableFeaturePointers];
                        // Spálil jsem na tom už hrozně času a nemůžu na to přijít, tak holt zde ručně vyfiltruji duplicity.
                        // Jde o to, že specificky při výběru WMS feature se tento event z nějakého důvodu vyvolá dvakrát
                        // (ze stejného místa) a i features jsou následně v eventu dvakrát.
                        allSelectedFeaturePointers = allSelectedFeaturePointers.filter((feature, index, self) =>
                            feature.featureId ? self.findIndex(f => f.featureId === feature.featureId) === index : true,
                        );
                        let overlayFeaturePointers = allSelectedFeaturePointers?.filter(feature => feature.layerId === OVERLAY_LAYER_ID);
                        let nonOverlayFeaturePointers = allSelectedFeaturePointers?.filter(feature => feature.layerId !== OVERLAY_LAYER_ID);
                        if (
                            [
                                EditorToolType.MOVE,
                                EditorToolType.EDIT_MOVE,
                                EditorToolType.OFFSET,
                                EditorToolType.ROTATE,
                                EditorToolType.ROTATE_MOVE,
                                EditorToolType.EDIT,
                            ].includes(event.toolType)
                        ) {
                            // Práva jsou vedena na datové (parent) vrstvě, kdežto výběr z mapy nese ID
                            // diskriminované podvrstvy. Bez převodu na efektivní vrstvu by se výběr nad
                            // diskriminovanou vrstvou vyhodnotil jako "bez práva UPDATE" a hned zase zrušil
                            // (objekt jen problikl). Overlay řešíme zvlášť, ať se do výběru nedostane dvakrát.
                            let selectedFeatureForUpdate = nonOverlayFeaturePointers.filter(layer =>
                                userLayerActions.allowedUpdateLayers?.some(i => getEffectiveLayerId(layersConfigQuery?.data, layer.layerId) === i),
                            );
                            if (JSON.stringify(nonOverlayFeaturePointers) !== JSON.stringify(selectedFeatureForUpdate)) {
                                ref.current.selectFeatures([...selectedFeatureForUpdate, ...overlayFeaturePointers]);
                            }
                        }
                        let layerId = nonOverlayFeaturePointers?.[0]?.layerId;
                        let featureId = nonOverlayFeaturePointers?.[0]?.featureId;
                        setDetailLayerId(layerId);
                        setDetailFeatureId(featureId);
                        setSelectedDetailFeature(nonOverlayFeaturePointers);
                        if (allSelectedFeaturePointers.length === 0 && ref.current.highlightFeature) {
                            ref.current.highlightFeature();
                        }
                    }
                    break;

                case EditorEventType.CUSTOM_ACTION:
                    if (event.toolId === ActionTypes.SHOW_FEAT_TABLE) {
                        setIsAttributeTableOpen(!isAttributeTableOpen);
                    }

                    if (event.toolId === EditorToolType.POINT_COORDS) {
                        break;
                    }
                    if (event.toolId === EditorToolType.LINE_OFFSET) {
                        handleLineOffsetModal(event.writableFeaturePointers, (event as any).options);
                        break;
                    }
                    if (event.toolId === EditorToolType.MERGE_LINES) {
                        Modal.show(({onClose}) => (
                            <MultipleFeaturesSelection
                                title={'Spojit do linie'}
                                onClose={onClose}
                                featurePointers={event.writableFeaturePointers}
                                onClick={feature => {
                                    ref.current.activateToolWithType(EditorToolType.MERGE_LINES, {featurePointers: [feature]});
                                }}
                            />
                        ));
                        break;
                    }
                    if (event.toolId === EditorToolType.PERPENDICULAR_LINE) {
                        handlePerpendicularLineModal(event.maxOffset, event.feature);
                        break;
                    }
                    if (event.toolId === ActionTypes.EXTENT_FEATURES) {
                        // Single-layer režim → featureIds (beze změny, pro AttributeTableFinal apod.)
                        if (event.featureIds !== undefined && event.featureIds !== extentFeatures) {
                            setExtentFeatures(event.featureIds);
                        }
                        // Režim "všechny zapnuté vrstvy" → pointery s layerId (panel hledání)
                        if (event.writableFeaturePointers !== undefined) {
                            setExtentFeaturePointers(event.writableFeaturePointers);
                        }
                    }
                    if (event.toolId === ActionTypes.TOOLS_ENABLED) {
                        setToolsEnabled(event.toolsEnabled);
                    }
                    // Handler pro aktualizaci dat měření geometrie
                    if (event.actionId === 'GEOMETRY_MEASUREMENT_UPDATE') {
                        setGeometryMeasurementData((event as any).measurementData);
                        setMeasurementFeaturePointers((event as any).featurePointers ?? []);
                        setHighlightedMeasurementEdge(null); // nové měření → zruš zvýraznění hrany
                    }

                    // Klik na hranu v mapě → zvýrazni odpovídající řádek v MeasurementCard;
                    // klik mimo hrany (partIndex=null) zvýraznění zruší.
                    if (event.actionId === 'MEASUREMENT_EDGE_SELECTED') {
                        const partIndex = (event as any).partIndex;
                        setHighlightedMeasurementEdge(partIndex == null ? null : {partIndex, edgeIndex: (event as any).edgeIndex});
                    }

                    if (event.actionId === 'NAVIGATE_TO_DTM_DETAIL') {
                        // @ts-ignore
                        history.push(getDtmDetailUrl(event?.data?.id));
                    }
                    // Klávesové zkratky historie geometrie přeposlané z enginu (fokus je uvnitř iframe mapy).
                    if (event.actionId === 'GEOMETRY_UNDO') {
                        geometryHistoryRef.current.undo();
                    }
                    if (event.actionId === 'GEOMETRY_REDO') {
                        geometryHistoryRef.current.redo();
                    }
                    break;

                case EditorEventType.ACTION:
                    switch (event.actionId) {
                        case ActionTypes.SHOW_FEAT_TABLE:
                            openAttributeTable(event.layerId);
                            break;
                        case ActionTypes.CHECKED_LAYER_EDIT:
                            setCheckedLayerId(event.layerId);
                            break;
                        case ActionTypes.NAVIGATE_TO_USER_LAYER_EDIT:
                        case ActionTypes.NAVIGATE_TO_USER_ACTION:
                            {
                                let {layerId, featureId, akceId, label} = event as any; // akceId neexistuje v EditorFeatureActionEvent
                                if (akceId) {
                                    await handleAttributesFormAction(layerId, featureId, akceId, label);
                                    break;
                                }
                                await handleProcessDetailAction(layerId, featureId);
                            }
                            break;
                        case ActionTypes.DISTANCE_CHANGED:
                            setDistanceData({
                                currentDistance: event.currentDistance,
                                currentPixelDistance: event.currentPixelDistance,
                                // @ts-ignore
                                currentZoomLevel: event.currentZoomLevel,
                            });
                            if (isAttributeTableOpen && isExtentChecked) {
                                ref.current.setLayerFeaturesFilter(FeatureFilterType.SHOW_EXTENT, activeFilterLayerId ?? selectedLayerId);
                            }
                            searchExtentRefreshRef.current?.();
                            break;
                        case ActionTypes.MULTIPLE_FEATURES_SELECT:
                            handleMultipleFeaturesSelection(event.featurePointers, event.totalCount, event.addToSelection);
                            break;
                        case ActionTypes.NETWORK_ERROR:
                            mapioContext.set(prevState => ({
                                ...prevState,
                                errors: event.errors,
                            }));
                            break;
                        case ActionTypes.DISABLE:
                            setDisabled(event.disable);
                            break;

                        case ActionTypes.OPTIMAL_PRINT_SCALE:
                            setOptimalPrintScale(event.scale);
                            break;
                        case ActionTypes.EXTERNAL_LAYER_TO_CATALOG:
                            if (event.externalLayers) {
                                mapioContext.set(prevState => ({
                                    ...prevState,
                                    activeLayers: [...prevState.activeLayers, ...event.externalLayers.map(layer => layer.id)],
                                    externalLayers: [...prevState.externalLayers, ...event.externalLayers],
                                    externalLayersCatalog: [...prevState.externalLayersCatalog, ...formatExternalLayersToCatalogItemVO(event.externalLayers)],
                                }));
                            }
                            break;
                        case ActionTypes.SHOW_TOASTER:
                            switch (event.intent) {
                                case 'warning':
                                    toaster.warningWithTitle(event.title, event.message);
                                    break;
                                case 'error':
                                    toaster.errorWithTitle(event.title, event.message);
                                    break;
                                case 'success':
                                    toaster.successWithTitle(event.title, event.message);
                                    break;
                            }
                            break;
                        case ActionTypes.CENTER_AND_ZOOM_CHANGED: {
                            if (!isIframe) {
                                const {zoom, center} = event;
                                if (zoom && center) {
                                    saveMapViewState({zoom, center});
                                }
                            }
                            // Přepočet výřezového seznamu SearchPanelu i při iniciálním nastavení pohledu (bez čekání
                            // na pohyb mapou) – jinak je seznam po prvním načtení prázdný, dokud uživatel nehne mapou.
                            searchExtentRefreshRef.current?.();
                            break;
                        }

                        // case 'SCALE_OPTIONS':
                        //     // @ts-ignore
                        //     setScaleOptions(event.scales);
                        //     break;
                        default:
                            console.warn('Neznámý actionId', (event as any).actionId);
                    }
                    break;

                case EditorEventType.DELETE:
                    if (isTempEditingStarted) {
                        return; // Probíhá dočasná editace, ignorujeme události
                    }
                    break;

                case EditorEventType.TOOL_ACTIVATED:
                    if (event.toolType === EditorToolType.SNAP) {
                        setSnappingActive(true);
                        return;
                    }
                    setActiveTool(event.toolType);
                    /**
                     * Při použití nástroje výběru "Obdélníkem" nebo "Polygonem" tak se vykonná tento case switch a event.toolType je rovno EXACT_SELECT nebo BOX_SELECT nebo POLY_SELECT z enumu EditorToolType.
                     * Což přenastaví state measurementToolInput této komponenty, který je předáván do child komponenty Sidebar a na to uvnitř něho zareaguje podmínka,
                     * která zavírala postranní panel s informacemi o vybraných objektech.
                     */
                    if (![EditorToolType.EXACT_SELECT, EditorToolType.BOX_SELECT, EditorToolType.POLY_SELECT, EditorToolType.PRINT].includes(event.toolType)) {
                        const measurementTools: string[] = [EditorTool.MEASURE_LINE.toString(), EditorTool.MEASURE_POLYGON.toString()];
                        const measurementToolActivated: boolean = measurementTools.includes(event.toolType);
                        const actionToTrigger = measurementToolActivated ? ETriggerAction.SHOW : ETriggerAction.HIDE;
                        setOtherToolsTrigger({...otherToolsTrigger, actionToTrigger});
                    }
                    break;

                case EditorEventType.TOOL_DEACTIVATED:
                    if (event.toolType === EditorToolType.SNAP) {
                        setSnappingActive(false);
                    }
                    if ([EditorToolType.PRINT, EditorToolType.LINE_EXTENSION].includes(event.toolType)) {
                        setActiveTool(null);
                    }
                    break;

                case EditorEventType.TOPIC_CHANGE:
                    setSelectedLayerId(null);
                    setActiveFilterLayerId(null);
                    setIsAttributeTableOpen(false);
                    break;
                case EditorEventType.MAP_INTERACTION:
                    if (event.interactionType === MapInteractionType.DRAG_PAN_FINISHED) {
                        if (isAttributeTableOpen && isExtentChecked) {
                            ref.current.setLayerFeaturesFilter(FeatureFilterType.SHOW_EXTENT, activeFilterLayerId ?? selectedLayerId);
                        }
                        searchExtentRefreshRef.current?.();
                    }
                    break;

                default:
                    console.warn('Přišel nevhodný request s typem:', event.type);
            }
        },
        [
            checkedLayerId,
            detailFeatureId,
            writableLayers,
            selectedLayerId,
            activeFilterLayerId,
            isExtentChecked,
            layersConfigQuery.data,
            topicsMapQuery.data,
            selectedDetailFeature,
            saveMapViewState,
        ],
    );

    return (
        <BlankPage currentPage={MAPA_PAGE_KEY}>
            {topicId ? (
                <WaitForQueryData queries={[topicsMapQuery]}>
                    {() => {
                        if (topicsMapQuery.isError) {
                            return <AppError message={`Selhalo načtení konfigurace téma ${topicId}`} />;
                        }
                        if (!topicsMapQuery.data.length) {
                            return <AppError message={`Aplikace potřebuje minimálně jedno téma s topicVisiblity VSE`} />;
                        }
                        // Kdyby v topicId bylo téma, které v konfiguraci není, vykreslíme první dostupné.
                        // Samotné přepnutí topicId řeší effect výše - v renderu se stav měnit nesmí.
                        const topic: TopicVO = currentTopic ?? topicsMapQuery.data[0];
                        // @ts-ignore
                        // if (__DEV__) {
                        //     //INTERNĚ V DEVELOPMENTU CHCEME VŽDY UMOŽNIT MĚNIT TOPIC JINAK PŘEKLIKNEŠ NĚKAM A NEJDE TO PŘENASTAVIT ZPĚT
                        //     catalogMenuVisible = true;
                        // }
                        const queryParams = Object.fromEntries(query.entries());
                        return (
                            <>
                                <MapEditor
                                    className={`h-100 w-100 border-0 ${IFRAME_CLASSNAME}`}
                                    clientMode={mapioContext?.value?.clientMode || ClientMode.NEW_CLIENT}
                                    ref={ref}
                                    tools={
                                        filterToolsByEnabled(
                                            currentTools,
                                            computedToolsEnabled,
                                            insertionLayerId,
                                            layersConfigQuery?.data,
                                        ) as typeof currentTools
                                    }
                                    // Zatim necham zakomentovaný, protože nevim co řádek nadtim rozbije... neměl by nic ale nevim to stopro
                                    // tools={flattenTools(currentTools) as EditorTool[]}
                                    layersMode={layersMode}
                                    onWritableLayersChange={onWritableLayersChange}
                                    onTopicChange={onTopicChange}
                                    onBackgroundLayerChange={onBackgroundLayerChange}
                                    onForegroundLayerChange={onForegroundLayerChange}
                                    onLayersChange={onLayersChange}
                                    featureDeleteHandler={req => handleDeleteAttributeForm(req.featurePointers)}
                                    serverUrl={
                                        globalThis.location.origin +
                                        (globalThis.location.pathname.split('/').length > 1 ? '/' + globalThis.location.pathname.split('/')[1] : '')
                                    }
                                    onEvent={handleFeatureEditorEvent}
                                    authentication={auth.login}
                                    rasterLayersPrintMode={LayerPrintMode.VISIBLE}
                                    vectorLayersPrintMode={LayerPrintMode.VISIBLE}
                                    externalLayersEnabled={true}
                                    queryParams={{
                                        topicVisibility: mapioContext.value.isIframe ? TopicVisibility.APLIKACE : TopicVisibility.VSE,
                                        isIframe: mapioContext.value.isIframe,
                                        ...queryParams,
                                        // Výchozí datumový filtr, ze kterého si mapa spočítá validFrom/validTo pro
                                        // úvodní stažení dat (MCW-18757). Až za ...queryParams, protože hodnotu z URL
                                        // je potřeba vyhodnotit - nepoužitelnou (CUSTOM_*, neznámé id) mapě neposíláme,
                                        // jinak by mapa nefiltrovala, ale tlačítko filtru hlásilo výchozí období.
                                        defaultDateFilter: getDefaultDateFilterId(queryParams),
                                    }} // skrytí starého UI
                                    layerSettingsEnabled={true}
                                    onReady={() => setDisabled(false)}
                                    contextParams={convertContextToParams(mapioContext.value.context)}
                                    initialTopic={topic?.id ?? topicId}
                                    contextTools={contextTools}
                                    featureTooltips={featureTooltips}
                                    initialLocation={initialSavedView?.center}
                                    initialZoom={initialSavedView?.zoom}
                                    initialLayers={initialSavedView?.activeLayers as number[]}
                                    initialBackgroundLayer={initialSavedView?.backgroundLayer as number}
                                    initialForegroundLayer={initialSavedView?.foregroundLayer as number}
                                    keyboardShortcuts={[
                                        KeyboardShortcut.DELETE,
                                        KeyboardShortcut.COPY,
                                        KeyboardShortcut.PASTE,
                                        KeyboardShortcut.CUT,
                                        KeyboardShortcut.SNAP,
                                        KeyboardShortcut.POINT,
                                        KeyboardShortcut.LINE,
                                        KeyboardShortcut.POLYGON,
                                        KeyboardShortcut.PRINT,
                                        KeyboardShortcut.EDIT,
                                        KeyboardShortcut.MOVE,
                                        KeyboardShortcut.LINE_OFFSET,
                                        KeyboardShortcut.SPLIT_LINE,
                                        KeyboardShortcut.MERGE_LINES,
                                        KeyboardShortcut.LINE_EXTENSION,
                                    ]}
                                    color={color}
                                />
                                {isCatalogMenuVisible && (
                                    <Sidebar
                                        backgroundLayer={backgroundLayer}
                                        coordinateLocation={coordinateLocation}
                                        detailFeatureId={detailFeatureId}
                                        detailLayerId={detailLayerId}
                                        foregroundLayer={foregroundLayer}
                                        handleFeatureEditorEvent={handleFeatureEditorEvent}
                                        handleUpdate={handleAttributesForm}
                                        catalogMenuVisible={catalogMenuVisible}
                                        otherToolsTrigger={otherToolsTrigger}
                                        setBackgroundLayer={setBackgroundLayer}
                                        setForegroundLayer={setForegroundLayer}
                                        dateFilterRange={dateFilterRange}
                                        setDateFilterRange={setDateFilterRange}
                                        dateFilterItems={dateFilterItems}
                                        topicChangeEnabled={isTopicChangeEnabled}
                                        optimalScale={optimalPrintScale}
                                        onBackToList={searchPanelBackToList}
                                        extentFeaturePointers={extentFeaturePointers}
                                        extentRefreshRef={searchExtentRefreshRef}
                                        onSearchItemSelected={backToListFn => setSearchPanelBackToList(() => backToListFn)}
                                        onSearchPanelClose={() => setSearchPanelBackToList(null)}
                                    />
                                )}
                                {!(noToolbar || (topic.publicSimpleView && serverPublic)) && (
                                    <Toolbar
                                        setOtherToolsTrigger={val => setOtherToolsTrigger({...otherToolsTrigger, ...val})} //chceme zachovat toolInfo
                                        toolsEnabled={computedToolsEnabled as EditorToolType[]}
                                        snappingActive={snappingActive}
                                        layersMode={layersMode}
                                        allowedInsertionLayers={userLayerActions.allowedInsertionLayers}
                                    />
                                )}
                                <Overhead
                                    backgroundLayer={backgroundLayer}
                                    setBackgroundLayer={setBackgroundLayer}
                                    foregroundLayer={foregroundLayer}
                                    setForegroundLayer={setForegroundLayer}
                                    undoSteps={undoStack}
                                    redoCount={redoStack.length}
                                    onUndoStep={undoToStep}
                                    onRedo={redoLastStep}
                                />
                                {distanceData && distanceData.currentDistance !== null && topic.showFooter && (
                                    <>
                                        {!isMobile && <ZoomSelector distanceData={distanceData} initialZoom={topic.initialZoom || 23} />}
                                        <InformationalFooter distanceData={distanceData} />
                                    </>
                                )}
                                {/* MeasurementCard je nezávislá na scale baru (distanceData) – zobrazuje se vždy, když
                                    přijdou data měření (i po výběru objektu z OVERLAY vrstvy, kde se detail nezobrazuje).
                                    Kartu vykreslujeme i v iframe režimu, samotný obsah se null-guarduje uvnitř komponenty. */}
                                {topic.showFooter && (
                                    <MeasurementCard
                                        measurementData={geometryMeasurementData}
                                        featurePointers={measurementFeaturePointers}
                                        highlightedEdge={highlightedMeasurementEdge}
                                        onHighlightFeature={ref.current?.highlightFeature}
                                    />
                                )}
                                {isAttributeTableOpen && (
                                    <AttributeTableFinal
                                        extentFeatures={extentFeatures}
                                        setActiveFilterLayerId={setActiveFilterLayerId}
                                        setIsOpen={setIsAttributeTableOpen}
                                        handleUpdate={handleAttributesForm}
                                        handleDelete={handleDeleteAttributeForm}
                                        handleMove={moveToFeatureHandler}
                                        selectedDetailFeature={isProvazatChecked ? selectedDetailFeature : []}
                                        isExtentChecked={isExtentChecked}
                                        setIsExtentChecked={setIsExtentChecked}
                                        isProvazatChecked={isProvazatChecked}
                                        setIsProvazatChecked={setIsProvazatChecked}
                                    />
                                )}
                                {isClientAttributeTableOpen && clientAttributeTableLayerId && (
                                    <ClientAttributeTableFinal
                                        layerId={clientAttributeTableLayerId}
                                        setIsOpen={setIsClientAttributeTableOpen}
                                        handleMove={moveToFeatureHandler}
                                        selectedDetailFeature={selectedDetailFeature}
                                    />
                                )}
                                {isImageGalleryOpen && (
                                    <LightboxGallery event={imageEventData} isOpen={isImageGalleryOpen} onClose={() => setIsImageGalleryOpen(false)} />
                                )}
                            </>
                        );
                    }}
                </WaitForQueryData>
            ) : (
                <Spinner />
            )}
        </BlankPage>
    );
};
